// ─────────────────────────────────────────────────────────────────────────────
// Post-Incident Audit — the home.
//
// WHY THIS IS ITS OWN SCREEN AND NOT A BUTTON IN A QUEUE.
//
// The first version of this feature hung off MDS Opportunities, because that is
// where the DATA was. That was the wrong reason. MDS Opportunities is a revenue
// queue — every row is a coding gap worth dollars — and a post-event compliance
// audit has a different trigger, a different reader and a different lifecycle. It
// also has a work list of its own ("who still needs auditing"), which a button
// inside somebody else's screen cannot carry.
//
// And it is a FAMILY. The facility's form is headed "Aspect of Care: ___" —
// physical restraints is one of a set, and falls, pressure ulcers and elopement are
// the same shape. A family of worksheets with a lifecycle needs a home.
//
// ── THE LOOP THIS SCREEN SERVES ─────────────────────────────────────────────
//
//   something happens (a risk finding, a fall, a restraint newly coded)
//     → the resident appears here, unaudited
//     → the nurse opens the worksheet; the system has pre-filled every row it
//       could find evidence for, and says why it could not fill the rest
//     → the nurse reads the chart and enters what they find
//     → they sign it, and it becomes the record of that cycle
//
// The list is the first two steps. RestraintWorksheet is the rest.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from "react";
import { fetchPostIncidentAudits } from "./postIncidentAuditApi";
import RestraintWorksheet from "./RestraintWorksheet";
// ONE WORKED EXAMPLE PER ASPECT, keyed by the same registry key the server uses.
//
// There used to be one sample — restraints — and sample mode refused to render it under
// any other heading, on the grounds that Section P evidence behind a FALLS title teaches
// the wrong worksheet. That refusal was right and is kept below for any aspect still
// without charts of its own; what changed is that four aspects now have them.
//
// Each file is generated from its OWN compiler over its own invented charts
// (build_falls_sample.mjs, build_pain_sample.mjs, and so on), so a falls sheet is real
// fallsAudit() output and cites J1800 and the incident log rather than restraint devices.
import SAMPLE_RESTRAINTS from "../demo/restraintAuditSample.json";
import SAMPLE_FALLS from "../demo/fallsAuditSample.json";
import SAMPLE_PAIN from "../demo/painAuditSample.json";
import SAMPLE_PRESSURE_ULCER from "../demo/pressureUlcerAuditSample.json";
import SAMPLE_ELOPEMENT from "../demo/elopementAuditSample.json";
import SAMPLE_HIGH_RISK_MEDS from "../demo/highRiskMedsAuditSample.json";
import SAMPLE_DIABETIC_CARE from "../demo/diabeticCareAuditSample.json";
import { previewMode, setPreviewMode, DataSourceSwitch } from "../demo/previewMode";
import { postIncidentAuditCcn, POST_INCIDENT_ASPECTS, postIncidentAspectLabel } from "./postIncidentAuditAccess";

const SAMPLES = {
  RESTRAINTS: SAMPLE_RESTRAINTS,
  FALLS: SAMPLE_FALLS,
  PAIN: SAMPLE_PAIN,
  PRESSURE_ULCER: SAMPLE_PRESSURE_ULCER,
  ELOPEMENT: SAMPLE_ELOPEMENT,
  HIGH_RISK_MEDS: SAMPLE_HIGH_RISK_MEDS,
  DIABETIC_CARE: SAMPLE_DIABETIC_CARE,
};

/**
 * The aspects of care this tool covers.
 *
 * The ones that are not built are LISTED rather than hidden. A screen showing one
 * worksheet reads as "this is the audit tool"; a screen showing one of five reads as
 * "this is the first of five", which is what it is. Hiding them would also mean the
 * next person cannot see that the shape was designed for more than restraints.
 */
/**
 * The same type scale the worksheet uses. Both halves of this tool are read in one
 * sitting -- the list, then a worksheet, then back -- so a size that changes between
 * them reads as two different products. See the note on T in RestraintWorksheet.jsx for
 * why these moved up; the short version is that 11 and 12px was too small for a form
 * somebody works down at a cart at the end of a shift.
 */
const T = { micro: 11.5, xs: 12.5, sm: 13.5, base: 14, md: 15, lg: 16, xl: 21 };
const LH = { tight: 1.4, body: 1.55, prose: 1.65 };

// The list lives in postIncidentAuditAccess now: Priority Scan offers the same
// choice, and two copies is how the two launch points end up with different sets.
const ASPECTS = POST_INCIDENT_ASPECTS;

const STATUS = {
  none: { label: "Not started", bg: "#fff7ed", fg: "#9a3412", border: "#fed7aa" },
  open: { label: "In progress", bg: "var(--ed-brand-soft)", fg: "var(--ed-info-deep)", border: "var(--ed-info-line)" },
  signed: { label: "Signed", bg: "var(--ed-success-soft)", fg: "#14532d", border: "#86efac" },
};

const statusOf = (a) => {
  if (!a.review) return "none";
  return a.review.completedAt ? "signed" : "open";
};

const Badge = ({ kind }) => {
  const s = STATUS[kind];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 20,
        background: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
        fontSize: T.xs,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
};

export default function PostIncidentAuditView({ session, activeFacility, ccn: ccnProp, showToast }) {
  // Resolved by the shared helper, not here — Priority Scan opens the same
  // worksheet and used a different preference order, which silently keyed reviews
  // to a facility identifier this screen does not list. See postIncidentAuditCcn.
  const ccn = postIncidentAuditCcn(activeFacility, ccnProp);
  const [aspect, setAspect] = useState("RESTRAINTS");
  const [state, setState] = useState({ loading: true });
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(null); // { residentId } or { sample }
  // SAMPLE DATA IS THE DEFAULT WHEN THERE IS NO FACILITY, and available always.
  // The worksheet is otherwise unteachable until a nightly scan has run against a
  // live connection, which is the state every new deployment is in.
  const [preview, setPreview] = useState(() => previewMode("post-incident-audit", !ccn));

  const load = useCallback(async () => {
    // THE SAMPLE FOR THIS ASPECT, OR NONE — never another aspect's.
    //
    // Each sample is generated from its own compiler over its own charts, so the rows,
    // the evidence and the citations belong to the worksheet on screen. The refusal below
    // is kept for any aspect added to the registry before its charts are written: showing
    // the restraint example under a falls title would put Section P evidence behind the
    // wrong heading, which is the confusion the aspect registry exists to prevent on the
    // server, arriving through the demo instead.
    const SAMPLE = SAMPLES[aspect] || null;
    if (preview && !SAMPLE) {
      const label = (ASPECTS.find((a) => a.key === aspect) || {}).label || aspect;
      return setState({
        loading: false,
        data: null,
        sampleUnavailable: `There is no worked example for ${label} yet — showing another aspect's charts here ` +
          "would teach the wrong rows. Switch the data source to Live to work this aspect against your own " +
          "facility, or pick an aspect that has an example.",
      });
    }
    // The sample is shaped exactly like the route's own response, so everything
    // below renders it by the same path live data takes.
    if (preview) {
      return setState({
        loading: false,
        data: {
          aspectOfCare: SAMPLE.aspectOfCare,
          // The demo describes the SAME clinical population the live screen does.
          // The fixtures are generated from the registry (build_*_sample.mjs), so
          // these cannot drift from what the API sends for the same aspect.
          // The worked example names everybody, so the note never draws there.
          namesRead: "all",
          cohort: SAMPLE.cohort,
          cohortNone: SAMPLE.cohortNone,
          fTags: SAMPLE.fTags,
          methodOfReview: SAMPLE.methodOfReview,
          threshold: SAMPLE.threshold,
          indicators: SAMPLE.indicators,
          residentsAudited: SAMPLE.residents.length,
          residentsWithRestraint: SAMPLE.residents.filter((r) => r.inUse).length,
          reviewsOpen: SAMPLE.residents.filter((r) => r.review && !r.review.completedAt).length,
          reviewsCompleted: SAMPLE.residents.filter((r) => r.review?.completedAt).length,
          audits: SAMPLE.residents
            .filter((r) => showAll || r.inUse)
            .map((r) => ({
              resident_id: r.residentId,
              resident_name: r.name,
              // NO ROOM NUMBER. The live route cannot send one — survai.resident has no
              // room column — so this rendered "112-A" beside every demo resident and
              // nothing at all beside a real one. A demo that shows a field the product
              // does not have teaches the product wrong, and this is the field a nurse
              // would most expect to keep. Wiring it needs a roster column first; until
              // then the sample shows what the screen can actually show.
              as_of: r.asOf,
              in_use: r.inUse,
              devices: r.devices,
              alarms: r.alarms,
              _sample: r,
              review: r.review
                ? {
                    id: r.review.id,
                    reviewer: r.review.reviewer,
                    reviewedOn: r.review.reviewedOn,
                    completedAt: r.review.completedAt,
                    triggerKind: r.review.triggerKind,
                  }
                : null,
            })),
          note: null,
        },
      });
    }
    if (!ccn) return setState({ loading: false, error: "No facility selected." });
    setState({ loading: true });
    try {
      const r = await fetchPostIncidentAudits(ccn, { all: showAll, aspect });
      setState({ loading: false, ...r });
    } catch (e) {
      setState({ loading: false, error: e.message });
    }
    // `aspect` IS A DEPENDENCY. Without it the chips changed colour and refetched
    // nothing — the switcher looked like it worked and showed the previous aspect's
    // residents under the new aspect's heading.
  }, [ccn, showAll, preview, aspect]);

  const togglePreview = () => {
    const next = !preview;
    setPreviewMode("post-incident-audit", next);
    setPreview(next);
    setOpen(null);
  };

  useEffect(() => {
    load();
  }, [load]);

  if (open) {
    return (
      <div>
        <button onClick={() => { setOpen(null); load(); }} style={{ marginBottom: 12 }}>
          ← Back to the audit list
        </button>
        <RestraintWorksheet
          ccn={ccn}
          aspect={aspect}
          residentId={open.residentId}
          residentName={open.residentName}
          triggerKind={open.triggerKind || "ROUTINE"}
          triggerRef={open.triggerRef || null}
          sample={open.sample || null}
          onClose={() => { setOpen(null); load(); }}
        />
      </div>
    );
  }

  const d = state.data || {};
  const audits = d.audits || [];
  const indicators = d.indicators || [];
  const never = indicators.filter((i) => i.neverCheckable);

  // ── ONE NAME FOR THE AUDIT, AND IT IS THE ONE THAT WAS CHOSEN ──────────────
  //
  // The picker reads POST_INCIDENT_ASPECTS ("Nursing — Falls", em dash); this heading
  // printed the server's `aspectOfCare`, which every compiler spells with a hyphen.
  // The worksheet was fixed and this screen was not, so the two headings a nurse sees
  // in one session still disagreed. Same fix, same reason.
  const aspectTitle = postIncidentAspectLabel(aspect) || d.aspectOfCare || "";

  // WHAT THE COUNT IS COUNTING, FROM THE ASPECT RATHER THAN FROM HERE.
  //
  // This sentence was hardcoded to restraints and rendered for all five aspects: on a
  // Falls worksheet it read "3 of 225 residents read have a restraint coded" under a
  // heading naming Falls. Reported from the live app. The fallback is deliberately
  // vague rather than restraint-shaped — an aspect whose server has not been deployed
  // yet should read as unspecific, not as another aspect's clinical population.
  const cohort = d.cohort || "this worksheet's criteria on the record";
  const cohortNone = d.cohortNone || null;

  // The aspect's own F-tags, from the registry. Falls derives its list from its rows,
  // so this cannot name a pathway the worksheet does not examine. Shape-tolerant
  // because the registry carries {tag, description} and an older deploy sent strings.
  // A COLUMN WITH NOTHING IN IT IS WORSE THAN NO COLUMN. "Coded device" is a
  // restraints idea: Section P's structured device columns belong to that aspect and
  // no other compiler fills them. On a Falls or Pain work list it was a header with a
  // dash under it on every row -- a column that looks like missing data rather than an
  // inapplicable question. Shown only when some row actually has a device.
  const anyDevices = (d.audits || []).some((a2) => (a2.devices || []).length);

  // ── A LIST OF NUMBERS HAS TO SAY WHY IT IS A LIST OF NUMBERS ───────────────
  //
  // Reported as "there are no names here?" against a work list of 174526 / 392739 /
  // 440645. The lookup was pointed at a table nothing writes (fixed server-side), but
  // the screen's half of the failure was that `resident_name || resident_id` renders a
  // bare id for THREE different reasons -- no name on file, the roster could not be
  // read, or the roster is simply not linked yet -- and says nothing about which. A
  // nurse reading it concludes the tool does not know who its residents are.
  //
  // namesRead comes from the API and distinguishes them. "all" draws nothing: a screen
  // that explains a problem nobody has is noise.
  const NAMES_NOTE = {
    none_on_file: "No resident names are on file for this facility yet, so this list shows PointClickCare ids. "
      + "Names arrive with the roster warm; the audits themselves are unaffected.",
    partial: "Some residents have no name on file yet and show their PointClickCare id instead. "
      + "Names arrive with the roster warm; the audits themselves are unaffected.",
    lookup_failed: "The resident roster could not be read on this request, so this list shows PointClickCare ids. "
      + "That is not the same as those residents having no name — it means the lookup did not run. "
      + "The audits themselves are unaffected.",
  };
  const namesNote = NAMES_NOTE[d.namesRead] || null;

  const fTags = (d.fTags || [])
    .map((t) => (typeof t === "string" ? t : t?.tag || t?.code || ""))
    .filter(Boolean);

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: T.xl, fontWeight: 800, color: "var(--ed-ink)" }}>Resident Post-Incident Audit</div>
        <div style={{ color: "var(--ed-muted)", fontSize: T.base, marginTop: 4, lineHeight: LH.body, maxWidth: 780 }}>
          After a risk or an event, these are the worksheets a nurse works down to confirm the required things are in
          place. Every row the record can answer is filled in before you open it; every row it cannot is marked with the
          reason, for you to enter.
        </div>
      </div>

      <DataSourceSwitch
        React={React}
        preview={preview}
        onToggle={togglePreview}
        note={
          preview
            ? "Showing a worked example — four residents chosen to put every cell state and every subtle rule on screen."
            : "Showing this facility's audits."
        }
      />

      {/* The family, with the unbuilt ones visible. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {ASPECTS.map((a) => (
          <button
            key={a.key}
            onClick={() => a.ready && setAspect(a.key)}
            disabled={!a.ready}
            title={a.ready ? a.label : "Not built yet — the worksheet shape is the same, the evidence rules are not written"}
            style={{
              padding: "7px 12px",
              borderRadius: 20,
              fontSize: T.sm,
              fontWeight: 700,
              cursor: a.ready ? "pointer" : "default",
              border: `1px solid ${aspect === a.key ? "#0f172a" : "#e2e8f0"}`,
              background: aspect === a.key ? "var(--ed-ink)" : "var(--ed-canvas)",
              color: aspect === a.key ? "var(--ed-canvas)" : a.ready ? "var(--ed-ink-2)" : "var(--ed-line-hover)",
            }}
          >
            {a.label}
            {!a.ready ? " · soon" : ""}
          </button>
        ))}
      </div>

      {state.loading ? <div style={{ padding: 18, color: "var(--ed-muted)" }}>Loading…</div> : null}

      {state.locked ? (
        <div style={{ padding: 16, background: "var(--ed-brand-soft)", border: "1px solid var(--ed-info-line)", borderRadius: 11, color: "var(--ed-info-deep)" }}>
          <strong>In preview.</strong> {state.reason || "Restricted to the system administrator."}
        </div>
      ) : null}

      {state.sampleUnavailable ? (
        <div style={{ padding: 16, background: "var(--ed-amber-soft)", border: "1px solid var(--ed-amber-line)", borderRadius: 11, color: "#78350f", lineHeight: LH.prose }}>
          {state.sampleUnavailable}
        </div>
      ) : null}

      {state.error ? (
        <div style={{ padding: 16, background: "var(--ed-alert-soft)", border: "1px solid #fca5a5", borderRadius: 11, color: "#7f1d1d" }}>
          {state.error} <button onClick={load} style={{ marginLeft: 10 }}>Retry</button>
        </div>
      ) : null}

      {!state.loading && !state.locked && !state.error ? (
        <>
          <div
            style={{
              padding: "13px 15px",
              border: "1px solid var(--ed-line)",
              borderRadius: 12,
              background: "var(--ed-bg)",
              marginBottom: 14,
            }}
          >
            <div style={{ fontWeight: 800, color: "var(--ed-ink)", fontSize: T.lg }}>
              Performance Improvement Monitoring · {aspectTitle}
            </div>
            <div style={{ color: "var(--ed-muted)", fontSize: T.sm, marginTop: 4 }}>
              Method of review: {d.methodOfReview} · {d.residentsWithRestraint ?? 0} of {d.residentsAudited ?? 0}{" "}
              residents read have {cohort} · {d.reviewsOpen ?? 0} audit
              {(d.reviewsOpen ?? 0) === 1 ? "" : "s"} in progress · {d.reviewsCompleted ?? 0} signed
            </div>

            {/* THE HONEST HEADLINE. Said before anybody opens a worksheet, so the tool
                does not look complete until it is used on a real resident. */}
            {never.length ? (
              <div
                style={{
                  marginTop: 11,
                  padding: "10px 12px",
                  background: "var(--ed-brand-soft)",
                  border: "1px solid var(--ed-info-line)",
                  borderRadius: 9,
                  color: "#1e3a8a",
                  fontSize: T.sm,
                  lineHeight: LH.body,
                }}
              >
                <strong>
                  {never.length} of {indicators.length} indicator{never.length === 1 ? " has" : "s have"} no readable
                  source at all
                </strong>{" "}
                — {never.map((i) => `#${i.n}`).join(", ")}. They arrive on the worksheet blank with the reason attached,
                and they are yours to enter.
                {/* THE ROWS AND THE TAGS COME FROM THIS ASPECT, NOT FROM RESTRAINTS.
                    This paragraph used to name the physician's order, the signed consent,
                    F604 and M1200C whatever worksheet was open — so a Falls audit cited
                    the restraints F-tag and pointed at restraint evidence. Reported from
                    the live app. Each unreadable row already carries its own `whyNot`
                    from the compiler, and the aspect already carries its own fTags, so
                    the honest version of this sentence is assembled from those. */}
                {never.some((i) => i.whyNot) ? (
                  <>
                    {" "}
                    The reasons given are:{" "}
                    {never
                      .filter((i) => i.whyNot)
                      .map((i) => `#${i.n} ${i.whyNot}`)
                      .join("; ")}
                    .
                  </>
                ) : null}
                {fTags.length ? (
                  <>
                    {" "}
                    This worksheet is examined under {fTags.join(", ")}, so it is a head start on the chart review, not
                    a replacement for it.
                  </>
                ) : (
                  " So it is a head start on the chart review, not a replacement for it."
                )}
              </div>
            ) : null}

            {d.note ? (
              <div style={{ marginTop: 10, color: "var(--ed-muted)", fontSize: T.sm, lineHeight: LH.body }}>{d.note}</div>
            ) : null}

            <label
              style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 11, fontSize: T.sm, color: "var(--ed-ink-3)" }}
            >
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Also list residents with no {cohort}
              <span style={{ color: "var(--ed-muted-2)" }}>(their worksheet reads N/A — evidence they were considered)</span>
            </label>
          </div>

          {namesNote && audits.length ? (
            <div
              role="note"
              style={{
                margin: "0 0 10px", padding: "9px 12px", borderRadius: 9,
                border: `1px solid ${d.namesRead === "lookup_failed" ? "#fde68a" : "#e2e8f0"}`,
                background: d.namesRead === "lookup_failed" ? "var(--ed-amber-soft)" : "var(--ed-bg)",
                color: d.namesRead === "lookup_failed" ? "#78350f" : "var(--ed-ink-3)",
                fontSize: T.sm, lineHeight: LH.body,
              }}
            >
              {namesNote}
            </div>
          ) : null}

          {audits.length === 0 ? (
            <div style={{ padding: 18, border: "1px dashed var(--ed-line-hover)", borderRadius: 12, color: "var(--ed-muted)", lineHeight: LH.prose }}>
              {/* "Nobody has a restraint" and "nobody has been read yet" are different
                  facts and must not share an empty state. */}
              {(d.residentsAudited ?? 0) === 0
                ? `The nightly scan has not read any resident for this facility yet, so there is nothing to list. That is not the same as a facility where nobody has ${cohort}.`
                : `${d.residentsAudited} resident(s) read and none has ${cohort}.${cohortNone ? ` ${cohortNone}` : ""}`}
            </div>
          ) : (
            <div style={{ border: "1px solid var(--ed-line)", borderRadius: 12, overflow: "hidden", background: "var(--ed-canvas)" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.md }}>
                  <thead>
                    <tr style={{ background: "var(--ed-bg)", color: "var(--ed-ink-3)", textAlign: "left" }}>
                      <th style={{ padding: "13px 15px", fontWeight: 700 }}>Resident</th>
                      {anyDevices ? (
                        <th style={{ padding: "13px 15px", fontWeight: 700 }}>Coded device</th>
                      ) : null}
                      <th style={{ padding: "13px 15px", fontWeight: 700 }}>Record read</th>
                      <th style={{ padding: "13px 15px", fontWeight: 700 }}>Audit</th>
                      <th style={{ padding: "13px 15px", fontWeight: 700 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {audits.map((a) => {
                      const st = statusOf(a);
                      return (
                        <tr key={`${a.resident_id}-${a.as_of}`} style={{ borderTop: "1px solid #eef2f7" }}>
                          <td style={{ padding: "15px 15px", fontWeight: 700, color: "var(--ed-ink)" }}>
                            {a.resident_name || a.resident_id}
                            {!a.in_use ? (
                              <span style={{ color: "var(--ed-muted-2)", fontWeight: 500, fontSize: T.xs }}> · no {cohort}</span>
                            ) : null}
                          </td>
                          {anyDevices ? (
                            <td style={{ padding: "15px 15px", color: "var(--ed-ink-3)" }}>
                              {(a.devices || []).length
                                ? (a.devices || []).map((x) => x.label).join(", ")
                                : <span style={{ color: "var(--ed-line-hover)" }}>—</span>}
                            </td>
                          ) : null}
                          <td style={{ padding: "15px 15px", color: "var(--ed-muted)", fontVariantNumeric: "tabular-nums" }}>
                            {/* A ROW THAT EXISTS ONLY BECAUSE SOMEBODY AUDITED IT. The nightly
                                scan has compiled no worksheet for this resident, so there is no
                                record-read date -- which is not the same as a blank cell, and
                                certainly not the same as "not audited". */}
                            {a.notCompiled ? (
                              <span style={{ color: "var(--ed-amber)" }} title="The nightly scan has not compiled a worksheet for this resident yet. The audit itself is saved.">
                                not scanned yet
                              </span>
                            ) : (
                              a.as_of || <span style={{ color: "var(--ed-line-hover)" }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: "15px 15px" }}>
                            <Badge kind={st} />
                            {a.review?.completedAt ? (
                              <div style={{ color: "var(--ed-muted-2)", fontSize: T.xs, marginTop: 3 }}>
                                {a.review.reviewedOn} · {a.review.reviewer}
                              </div>
                            ) : null}
                            {a.review?.triggerKind && a.review.triggerKind !== "ROUTINE" ? (
                              <div style={{ color: "var(--ed-muted-2)", fontSize: T.xs, marginTop: 2 }}>
                                after {a.review.triggerKind.toLowerCase()}
                              </div>
                            ) : null}
                          </td>
                          <td style={{ padding: "15px 15px", textAlign: "right" }}>
                            <button
                              onClick={() =>
                                setOpen({
                                  residentId: a.resident_id,
                                  residentName: a.resident_name || null,
                                  sample: a._sample || null,
                                })
                              }
                              style={{
                                padding: "6px 12px",
                                borderRadius: 8,
                                fontWeight: 700,
                                fontSize: T.sm,
                                border: "1px solid var(--ed-ink)",
                                background: st === "none" ? "var(--ed-ink)" : "var(--ed-canvas)",
                                color: st === "none" ? "var(--ed-canvas)" : "var(--ed-ink)",
                                cursor: "pointer",
                              }}
                            >
                              {preview ? "View" : st === "none" ? "Audit" : st === "open" ? "Continue" : "View"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
